<div class="modal-padding">
  <lib-reactive-form-group [bindTo]="bindTo" [mergeKey]="mergeKey">
    <lib-reactive-form-column-layout [nColumns]="2">
      <lib-reactive-form-searchable-drop-down
        [inputName]="'productType'"
        [label]="'Product Type'"
        [placeholder]="'Select a product type'"
        [searchPlaceholder]="'Search product types'"
        [inlineLabel]="false"
        [disabled]="viewModel.isViewOnly$ | push"
        [required]="false"
        [dropdowns]="viewModel.dropdownProductTypes$ | push"
        [tooltip]="'The smart filter will contain all products that match this product type.'"
        [bindingProperty]="'productType'"
        [dispersedKey]="mergeKey"
        [clearable]="!(viewModel.isViewOnly$ | push)"
        [useClearableButton]="false"
        [addToAtLeastNItemsFilledInPool]="true"
        [emitInitializationValuesAsValueChanged]="true"
        (valueChanged)="viewModel.connectToSelectedProductType($event)">
      </lib-reactive-form-searchable-drop-down>

      <lib-reactive-form-pill-drop-down
        [bindingProperty]="'variantTypes'"
        [inputName]="'variantTypes'"
        [label]="'Variant Types'"
        [placeholder]="'Select Variant Types to filter'"
        [emptyPlaceholder]="'Select a product type first'"
        [inlineLabel]="false"
        [disabled]="!(viewModel.variantTypesEnabled$ | async)"
        [tooltip]="'The smart filter will contain all products that match any of the variant types.'"
        [addToAtLeastNItemsFilledInPool]="true"
        [dispersedKey]="mergeKey"
        [clearable]="true"
        [mergeDelimitedItemsThatAreTheSameWithDifferentCasing]="{
          on: true,
          showMergedOptionInListWith: 'MostCapitalized',
          whichAnswerIsUsedAsValue: 'LeastCapitalized'
        }"
        [maxCharactersInSearch]="256"
        [limitNumberOfSelectedItemsTo]="50"
        [userCanAddNewDelimitedItems]="false"
        [submitAsDelimitedString]="false"
        [programmaticallyChangeValue]="viewModel.resetVariantTypes$ | push"
        [delimitedDropDownOptions]="viewModel.variantTypesCommaDelimitedString$ | push">
      </lib-reactive-form-pill-drop-down>
    </lib-reactive-form-column-layout>

    <lib-reactive-form-pill-drop-down
      [bindingProperty]="'strainTypes'"
      [inputName]="'strainTypes'"
      [label]="'Strain Types'"
      [placeholder]="'Select Strain Types to filter'"
      [inlineLabel]="false"
      [disabled]="viewModel.isViewOnly$ | async"
      [tooltip]="'The smart filter will contain all products that match any of the strain types.'"
      [addToAtLeastNItemsFilledInPool]="true"
      [dispersedKey]="mergeKey"
      [clearable]="true"
      [mergeDelimitedItemsThatAreTheSameWithDifferentCasing]="{
        on: true,
        showMergedOptionInListWith: 'MostCapitalized',
        whichAnswerIsUsedAsValue: 'LeastCapitalized'
      }"
      [maxCharactersInSearch]="256"
      [limitNumberOfSelectedItemsTo]="50"
      [userCanAddNewDelimitedItems]="false"
      [submitAsDelimitedString]="false"
      [delimitedDropDownOptions]="viewModel.strainTypesCommaDelimitedString$ | push">
    </lib-reactive-form-pill-drop-down>

    <div class="packaged-quantities-wrapper">
      <div class="packaged-quantities-input">
        <ng-container
          *rxIf="!(viewModel.useRangedPackageQuantities$ | push); strategy: 'immediate'; else: rangedInputs">
          <lib-reactive-form-pill-drop-down
            [bindingProperty]="'packagedQuantities'"
            [inputName]="'packagedQuantities'"
            [label]="'Packaged Quantity'"
            [placeholder]="'The number of units in a package'"
            [inlineLabel]="false"
            [disabled]="viewModel.isViewOnly$ | async"
            [tooltip]="'The smart filter will include products that exactly match one of the specified packaged quantities'"
            [addToAtLeastNItemsFilledInPool]="true"
            [dispersedKey]="mergeKey"
            [clearable]="true"
            [limitNumberOfSelectedItemsTo]="50"
            [hideAllItemsFromSearchList]="true"
            [userCanAddNewDelimitedItems]="true"
            [submitAsDelimitedString]="false"
            [newlyAddedDelimitedItemsMustBeNumbers]="{
              on: true,
              integersOnly: true,
              positiveOnly: true,
              maxCharacters: 4,
              forceMinValue: 1,
              forceMaxValue: 9999,
              outputArrayElementsAs: 'numbers'
            }">
          </lib-reactive-form-pill-drop-down>
        </ng-container>

        <ng-template #rangedInputs>
          <lib-reactive-form-number
            [bindingProperty]="'minPackagedQuantity'"
            [inputName]="'minPackagedQuantity'"
            [label]="'Minimum Packaged Quantity'"
            [placeholder]="'The minimum number of units in a package'"
            [inlineLabel]="false"
            [minValue]="1"
            [maxValue]="viewModel.maxPackagedQuantity$ | push"
            [disabled]="viewModel.isViewOnly$ | async"
            [tooltip]="'Include products with packaged quantities greater than or equal to this minimum value.'"
            [integersOnly]="true"
            [removeLabelFromErrorMessage]="true"
            [showErrorMessagesOnInitialization]="true"
            [addToAtLeastNItemsFilledInPool]="true"
            [dispersedKey]="mergeKey"
            [emitInitializationValuesAsValueChanged]="true"
            (valueChanged)="viewModel.connectToMinPackagedQuantity($event)">
          </lib-reactive-form-number>

          <lib-reactive-form-number
            [bindingProperty]="'maxPackagedQuantity'"
            [inputName]="'maxPackagedQuantity'"
            [label]="'Maximum Packaged Quantity'"
            [placeholder]="'The maximum number of units in a package'"
            [inlineLabel]="false"
            [disabled]="viewModel.isViewOnly$ | async"
            [tooltip]="'Include products with packaged quantities less than or equal to this maximum value.'"
            [minValue]="viewModel.minPackagedQuantity$ | push"
            [integersOnly]="true"
            [addToAtLeastNItemsFilledInPool]="true"
            [dispersedKey]="mergeKey"
            [removeLabelFromErrorMessage]="true"
            [showErrorMessagesOnInitialization]="true"
            [emitInitializationValuesAsValueChanged]="true"
            (valueChanged)="viewModel.connectToMaxPackagedQuantity($event)">
          </lib-reactive-form-number>
        </ng-template>
      </div>

      <div class="packaged-quantities-switch">
        <lib-reactive-form-switch
          [bindingProperty]="'useRangedPackageQuantities'"
          [inputName]="'useRangedPackageQuantities'"
          [label]="'Use Package Quantity Range'"
          [dispersedKey]="mergeKey"
          [tooltip]="'Enable to filter products by a minimum and maximum packaged quantity instead of exact values.'"
          [emitInitializationValuesAsValueChanged]="true"
          (valueChanged)="viewModel.connectToUseRangedPackageQuantities($event)">
        </lib-reactive-form-switch>
      </div>
    </div>

    <div class="unit-sizes-wrapper">
      <div class="unit-sizes-input">
        <ng-container *rxIf="!(viewModel.useRangedUnitSizes$ | push); else rangedUnitSizes">
          <lib-reactive-form-pill-drop-down
            [bindingProperty]="'unitSizes'"
            [inputName]="'unitSizes'"
            [label]="'Unit Size'"
            [placeholder]="'Enter one or more exact unit sizes'"
            [inlineLabel]="false"
            [disabled]="viewModel.isViewOnly$ | async"
            [tooltip]="'Include products that exactly match one of the specified unit sizes.'"
            [addToAtLeastNItemsFilledInPool]="true"
            [dispersedKey]="mergeKey"
            [clearable]="true"
            [limitNumberOfSelectedItemsTo]="50"
            [hideAllItemsFromSearchList]="true"
            [userCanAddNewDelimitedItems]="true"
            [submitAsDelimitedString]="false"
            [newlyAddedDelimitedItemsMustBeNumbers]="{
              on: true,
              maxCharacters: 5,
              maxNumberOfDecimals: 2,
              alwaysOutputNDecimalPlaces: 2,
              forceMinValue: 0,
              forceMaxValue: 999,
              outputArrayElementsAs: 'numbers'
            }">
          </lib-reactive-form-pill-drop-down>
        </ng-container>

        <ng-template #rangedUnitSizes>
          <lib-reactive-form-number
            [bindingProperty]="'minUnitSize'"
            [dispersedKey]="mergeKey"
            [inputName]="'minUnitSize'"
            [label]="'Minimum Unit Size'"
            [placeholder]="'Enter the minimum unit size'"
            [inlineLabel]="false"
            [minValue]="0.01"
            [maxValue]="viewModel.maxUnitSize$ | push"
            [step]="0.01"
            [disabled]="viewModel.isViewOnly$ | async"
            [addToAtLeastNItemsFilledInPool]="true"
            [tooltip]="'Include products with unit sizes greater than or equal to this minimum value.'"
            [removeLabelFromErrorMessage]="true"
            [showErrorMessagesOnInitialization]="true"
            [emitInitializationValuesAsValueChanged]="true"
            (valueChanged)="viewModel.connectToMinUnitSize($event)">
          </lib-reactive-form-number>
          <lib-reactive-form-number
            [bindingProperty]="'maxUnitSize'"
            [dispersedKey]="mergeKey"
            [inputName]="'maxUnitSize'"
            [label]="'Maximum Unit Size'"
            [placeholder]="'Enter the maximum unit size'"
            [inlineLabel]="false"
            [minValue]="viewModel.minUnitSize$ | push"
            [step]="0.01"
            [disabled]="viewModel.isViewOnly$ | async"
            [addToAtLeastNItemsFilledInPool]="true"
            [tooltip]="'Include products with unit sizes less than or equal to this maximum value.'"
            [removeLabelFromErrorMessage]="true"
            [showErrorMessagesOnInitialization]="true"
            [emitInitializationValuesAsValueChanged]="true"
            (valueChanged)="viewModel.connectToMaxUnitSize($event)">
          </lib-reactive-form-number>
        </ng-template>
      </div>

      <div class="unit-sizes-switch">
        <lib-reactive-form-switch
          [inputName]="'useRangedUnitSizes'"
          [label]="'Use Unit Size Range'"
          [bindingProperty]="'useRangedUnitSizes'"
          [dispersedKey]="mergeKey"
          [tooltip]="'Enable to filter products by a minimum and maximum value for the size of each unit instead of exact size of the unit.'"
          [emitInitializationValuesAsValueChanged]="true"
          (valueChanged)="viewModel.connectToUseRangedUnitSizes($event)">
        </lib-reactive-form-switch>
      </div>
    </div>

    <lib-reactive-form-column-layout [nColumns]="2">
      <lib-reactive-form-pill-drop-down
        [bindingProperty]="'productNames'"
        [inputName]="'productNames'"
        [label]="'Product Names'"
        [placeholder]="'Terms within product name (ie: OG, Kush)'"
        [inlineLabel]="false"
        [disabled]="viewModel.isViewOnly$ | async"
        [tooltip]="'The smart filter will include products that contain any of the specified search terms in the product name'"
        [addToAtLeastNItemsFilledInPool]="true"
        [dispersedKey]="mergeKey"
        [clearable]="true"
        [maxCharactersInSearch]="256"
        [limitNumberOfSelectedItemsTo]="50"
        [hideAllItemsFromSearchList]="true"
        [userCanAddNewDelimitedItems]="true"
        [submitAsDelimitedString]="false">
      </lib-reactive-form-pill-drop-down>
      <lib-reactive-form-pill-drop-down
        [bindingProperty]="'brandNames'"
        [inputName]="'brandNames'"
        [label]="'Brand Names'"
        [placeholder]="'Brand Names (Wana, Wana Quick)'"
        [inlineLabel]="false"
        [disabled]="viewModel.isViewOnly$ | async"
        [tooltip]="'The smart filter will include products that exactly match one of the specified brand names'"
        [addToAtLeastNItemsFilledInPool]="true"
        [dispersedKey]="mergeKey"
        [clearable]="true"
        [mergeDelimitedItemsThatAreTheSameWithDifferentCasing]="{
          on: true,
          showMergedOptionInListWith: 'MostCapitalized',
          whichAnswerIsUsedAsValue: 'LeastCapitalized'
        }"
        [maxCharactersInSearch]="256"
        [limitNumberOfSelectedItemsTo]="50"
        [userCanAddNewDelimitedItems]="true"
        [submitAsDelimitedString]="false"
        [delimitedDropDownOptions]="viewModel.universalVariantBrandsDelimited$ | push">
      </lib-reactive-form-pill-drop-down>

      <lib-reactive-form-pill-drop-down
        [bindingProperty]="'strains'"
        [inputName]="'strains'"
        [label]="'Strains'"
        [placeholder]="'Strain Names (White Widow, Pink Kush)'"
        [inlineLabel]="false"
        [disabled]="viewModel.isViewOnly$ | async"
        [tooltip]="'The smart filter will include products that exactly match one of the specified strains'"
        [addToAtLeastNItemsFilledInPool]="true"
        [dispersedKey]="mergeKey"
        [clearable]="true"
        [mergeDelimitedItemsThatAreTheSameWithDifferentCasing]="{
          on: true,
          showMergedOptionInListWith: 'MostCapitalized',
          whichAnswerIsUsedAsValue: 'LeastCapitalized'
        }"
        [maxCharactersInSearch]="256"
        [limitNumberOfSelectedItemsTo]="50"
        [userCanAddNewDelimitedItems]="true"
        [submitAsDelimitedString]="false"
        [delimitedDropDownOptions]="viewModel.strainsDelimited$ | push">
      </lib-reactive-form-pill-drop-down>
      <lib-reactive-form-pill-drop-down
        [bindingProperty]="'lineages'"
        [inputName]="'lineages'"
        [label]="'Lineages'"
        [placeholder]="'Select Strain Lineages (Purple Kush X Blue Dream) to filter'"
        [inlineLabel]="false"
        [disabled]="viewModel.isViewOnly$ | async"
        [tooltip]="'The smart filter will contain all products that match any of the strain lineages.'"
        [addToAtLeastNItemsFilledInPool]="true"
        [dispersedKey]="mergeKey"
        [clearable]="true"
        [mergeDelimitedItemsThatAreTheSameWithDifferentCasing]="{
          on: true,
          showMergedOptionInListWith: 'MostCapitalized',
          whichAnswerIsUsedAsValue: 'LeastCapitalized'
        }"
        [maxCharactersInSearch]="256"
        [limitNumberOfSelectedItemsTo]="50"
        [userCanAddNewDelimitedItems]="true"
        [submitAsDelimitedString]="false"
        [delimitedDropDownOptions]="viewModel.lineagesDelimited$ | push">
      </lib-reactive-form-pill-drop-down>
    </lib-reactive-form-column-layout>
    <lib-reactive-form-pill-drop-down
      [addToAtLeastNItemsFilledInPool]="true"
      [inputName]="'searchTerms'"
      [label]="'General Search Terms'"
      [placeholder]="'Search Terms (ie: Full-Spectrum, Pinene)'"
      [inlineLabel]="false"
      [disabled]="viewModel.isViewOnly$ | async"
      [tooltip]="
        'The smart filter will include products that contain any of the specified search terms in ' +
        'the products manufacturer, description or terpenes fields'
      "
      [bindingProperty]="'searchTerms'"
      [dispersedKey]="mergeKey"
      [clearable]="true"
      [maxCharactersInSearch]="256"
      [limitNumberOfSelectedItemsTo]="50"
      [userCanAddNewDelimitedItems]="true"
      [submitAsDelimitedString]="false"
      [hideAllItemsFromSearchList]="true">
    </lib-reactive-form-pill-drop-down>
    <lib-reactive-form-column-layout [nColumns]="2">
      <lib-reactive-form-number
        [inputName]="'minPrice'"
        [label]="'Minimum Price'"
        [placeholder]="'Minimum price of products'"
        [inlineLabel]="false"
        [required]="false"
        [disabled]="viewModel.isViewOnly$ | push"
        [integersOnly]="false"
        [step]="0.01"
        [tooltip]="'The smart filter will include products with a price greater than or equal to this value.'"
        [bindingProperty]="'minPrice'"
        [showErrorMessagesOnInitialization]="true"
        [minValue]="0"
        [maxValue]="viewModel.maxPrice$ | push"
        [emitInitializationValuesAsValueChanged]="true"
        (valueChanged)="viewModel.connectToMinPrice($event)">
      </lib-reactive-form-number>

      <lib-reactive-form-number
        [inputName]="'maxPrice'"
        [label]="'Maximum Price'"
        [placeholder]="'Maximum price of products'"
        [inlineLabel]="false"
        [required]="false"
        [integersOnly]="false"
        [step]="0.01"
        [disabled]="viewModel.isViewOnly$ | push"
        [tooltip]="'The smart filter will include products with a price less than or equal to this value.'"
        [bindingProperty]="'maxPrice'"
        [minValue]="viewModel.minPrice$ | push"
        [showErrorMessagesOnInitialization]="true"
        [emitInitializationValuesAsValueChanged]="true"
        (valueChanged)="viewModel.connectToMaxPrice($event)">
      </lib-reactive-form-number>

      <lib-reactive-form-number
        [inputName]="'minSecondaryPrice'"
        [label]="'Minimum Secondary Price'"
        [placeholder]="'Minimum Secondary price of products'"
        [inlineLabel]="false"
        [required]="false"
        [disabled]="viewModel.isViewOnly$ | push"
        [minValue]="0"
        [maxValue]="viewModel.maxSecondaryPrice$ | push"
        [step]="0.01"
        [tooltip]="'The smart filter will include products with a secondary price greater than or equal to this value.'"
        [addToAtLeastNItemsFilledInPool]="true"
        [showErrorMessagesOnInitialization]="true"
        [bindingProperty]="'minSecondaryPrice'"
        [dispersedKey]="mergeKey"
        [emitInitializationValuesAsValueChanged]="true"
        (valueChanged)="viewModel.connectToMinSecondaryPrice($event)">
      </lib-reactive-form-number>

      <lib-reactive-form-number
        [inputName]="'maxSecondaryPrice'"
        [label]="'Maximum Secondary Price'"
        [placeholder]="'Maximum Secondary price of products'"
        [inlineLabel]="false"
        [required]="false"
        [minValue]="viewModel.minSecondaryPrice$ | push"
        [disabled]="viewModel.isViewOnly$ | push"
        [step]="0.01"
        [tooltip]="'The smart filter will include products with a secondary price less than or equal to this value.'"
        [addToAtLeastNItemsFilledInPool]="true"
        [showErrorMessagesOnInitialization]="true"
        [bindingProperty]="'maxSecondaryPrice'"
        [dispersedKey]="mergeKey"
        [emitInitializationValuesAsValueChanged]="true"
        (valueChanged)="viewModel.connectToMaxSecondaryPrice($event)">
      </lib-reactive-form-number>

      <lib-reactive-form-number
        [inputName]="'minInventory'"
        [label]="'Minimum Inventory'"
        [placeholder]="'Minimum inventory of product'"
        [inlineLabel]="false"
        [integersOnly]="true"
        [disabled]="viewModel.isViewOnly$ | async"
        [minValue]="1"
        [maxValue]="viewModel.maxInventory$ | async"
        [tooltip]="'The smart filter will include products with inventory values greater than or equal to this value'"
        [addToAtLeastNItemsFilledInPool]="true"
        [showErrorMessagesOnInitialization]="true"
        [bindingProperty]="'minInventory'"
        [dispersedKey]="mergeKey"
        [emitInitializationValuesAsValueChanged]="true"
        (valueChanged)="viewModel.setMinInventory($event)">
      </lib-reactive-form-number>

      <lib-reactive-form-number
        [inputName]="'maxInventory'"
        [label]="'Maximum Inventory'"
        [placeholder]="'Maximum inventory of product'"
        [inlineLabel]="false"
        [integersOnly]="true"
        [disabled]="viewModel.isViewOnly$ | async"
        [tooltip]="'The smart filter will include products with inventory values less than or equal to this value'"
        [addToAtLeastNItemsFilledInPool]="true"
        [showErrorMessagesOnInitialization]="true"
        [bindingProperty]="'maxInventory'"
        [dispersedKey]="mergeKey"
        [minValue]="viewModel.minInventory$ | async"
        [emitInitializationValuesAsValueChanged]="true"
        (valueChanged)="viewModel.setMaxInventory($event)">
      </lib-reactive-form-number>
    </lib-reactive-form-column-layout>
  </lib-reactive-form-group>
</div>
